import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAccount, useDisconnect } from 'wagmi'
import { useAppKit } from '@reown/appkit/react'
import { ArrowLeftRight, History, Wallet, Menu, X, Shield, Globe, LogOut, Sun, Moon, ChevronDown, Home, Repeat } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { cn, shortenAddress } from '@/lib/utils'
import { useLanguage } from '@/i18n'
import { useAuthStore } from '@/store/useAuthStore'
import { useTheme } from '@/contexts/ThemeContext'
import { useBridgeConfig } from '@/hooks/useBridgeConfig'
import { MobileDrawer } from './MobileDrawer'

const languages = [
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
] as const

export function MainLayout() {
  const location = useLocation()
  const { address, isConnected } = useAccount()
  const { open } = useAppKit()
  const { disconnect } = useDisconnect()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const { language, setLanguage, t } = useLanguage()
  const { isAdmin, isAuthenticated, isAdminCandidate, checkAuth, refreshAdminCandidate } = useAuthStore()
  const { theme, toggleTheme } = useTheme()
  const langMenuRef = useRef<HTMLDivElement>(null)

  // On every wallet change: validate the JWT against the current wallet (drops
  // it on mismatch) AND ask the backend whether this address is registered as
  // admin in walletHolder. Both run in parallel.
  useEffect(() => {
    checkAuth(address)
    refreshAdminCandidate(address)
  }, [address])

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setLangMenuOpen(false)
      }
    }
    if (langMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [langMenuOpen])

  // Close the mobile drawer on route change so users don't have to tap close
  // after navigating from inside it.
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Backend-authoritative: button shows iff the connected wallet is registered
  // as admin in the walletHolder table (pre-SIWE) OR the current JWT matches
  // the connected wallet and was issued to an admin (post-SIWE).
  const showAdminButton = isConnected && (isAdminCandidate || (isAuthenticated && isAdmin))

  const currentLang = languages.find((l) => l.code === language) || languages[0]

  const { p2pEnabled } = useBridgeConfig()
  const navItems = [
    { path: '/', label: language === 'es' ? 'Inicio' : 'Home', icon: Home },
    { path: '/bridge', label: t('bridge'), icon: ArrowLeftRight },
    ...(p2pEnabled ? [{ path: '/p2p', label: t('p2p'), icon: Repeat }] : []),
    { path: '/history', label: t('history'), icon: History },
  ]

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className={cn(
        'border-b backdrop-blur-xl sticky top-0 z-50',
        theme === 'dark'
          ? 'border-dark-800/50 bg-dark-950/80'
          : 'border-gray-200 bg-white/80'
      )}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 h-14 flex items-center justify-between gap-2">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group min-w-0">
            <div className="w-8 h-8 bg-gradient-to-br from-uni-500 to-uni-600 rounded-lg flex items-center justify-center shadow-glow-sm shrink-0">
              <ArrowLeftRight className="w-4 h-4 text-dark-950" />
            </div>
            <span className={cn(
              'font-display font-semibold truncate',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>W2USDT <span className="text-uni-500">Bridge</span></span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                    isActive
                      ? 'bg-uni-500/15 text-uni-500'
                      : theme === 'dark'
                        ? 'text-dark-400 hover:text-white hover:bg-dark-800/50'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              )
            })}
          </nav>

          {/* Right Section: desktop shows full controls, mobile shows only
              hamburger (everything else lives in the drawer). */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle — desktop only */}
            <button
              onClick={toggleTheme}
              className={cn(
                'hidden md:inline-flex p-2 rounded-lg transition-all',
                theme === 'dark'
                  ? 'bg-dark-800/50 border border-dark-700 hover:border-dark-600 text-dark-300 hover:text-white'
                  : 'bg-white border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              )}
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Language Dropdown — desktop only */}
            <div className="relative hidden md:block" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-all',
                  theme === 'dark'
                    ? 'bg-dark-800/50 border border-dark-700 hover:border-dark-600 text-dark-300 hover:text-white'
                    : 'bg-white border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
                )}
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm">{currentLang.flag}</span>
                <ChevronDown className={cn('w-3 h-3 transition-transform', langMenuOpen && 'rotate-180')} />
              </button>

              {langMenuOpen && (
                <div className={cn(
                  'absolute right-0 top-full mt-2 w-40 rounded-xl shadow-xl border overflow-hidden z-50',
                  theme === 'dark'
                    ? 'bg-dark-800 border-dark-700'
                    : 'bg-white border-gray-200'
                )}>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code as 'es' | 'en')
                        setLangMenuOpen(false)
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                        language === lang.code
                          ? 'bg-uni-500/15 text-uni-500'
                          : theme === 'dark'
                            ? 'text-dark-300 hover:bg-dark-700 hover:text-white'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      )}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Admin Button — desktop only */}
            {showAdminButton && (
              <Link
                to="/admin/dashboard"
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/25 transition-all"
              >
                <Shield className="w-4 h-4" />
                <span>{t('adminPanel')}</span>
              </Link>
            )}

            {/* Wallet / Connect — desktop only (mobile gets it in the drawer) */}
            {isConnected ? (
              <div className="hidden md:flex items-center gap-1">
                <div className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm border',
                  theme === 'dark'
                    ? 'bg-dark-800/80 border-dark-700 text-white'
                    : 'bg-white border-gray-200 text-gray-900'
                )}>
                  <Wallet className="w-4 h-4" />
                  <span>{shortenAddress(address || '')}</span>
                </div>
                <button
                  onClick={() => disconnect()}
                  className="p-2 rounded-lg bg-red-500/15 border border-red-500/20 text-red-500 hover:bg-red-500/25 transition-all"
                  title={language === 'es' ? 'Desconectar' : 'Disconnect'}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => open()}
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm bg-gradient-to-r from-uni-600 to-uni-500 text-dark-950 shadow-glow-sm hover:shadow-glow"
              >
                <Wallet className="w-4 h-4" />
                <span>{t('connect')}</span>
              </button>
            )}

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menú"
              className={cn(
                'md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg',
                theme === 'dark'
                  ? 'bg-dark-800/50 text-dark-300 hover:text-white'
                  : 'bg-gray-100 text-gray-600 hover:text-gray-900'
              )}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className={cn(
        'border-t py-6',
        theme === 'dark'
          ? 'border-dark-800/50'
          : 'border-gray-200'
      )}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div className={cn(
              'text-sm',
              theme === 'dark' ? 'text-dark-500' : 'text-gray-400'
            )}>
              <span>© {new Date().getFullYear()} W2USDT Bridge</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <Link
                to="/privacy"
                className={cn(
                  'text-sm transition-colors',
                  theme === 'dark'
                    ? 'text-dark-400 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                )}
              >
                {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
              </Link>
              <Link
                to="/terms"
                className={cn(
                  'text-sm transition-colors',
                  theme === 'dark'
                    ? 'text-dark-400 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                )}
              >
                {language === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions'}
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Drawer */}
      <MobileDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        side="right"
        panelClassName={cn(
          theme === 'dark'
            ? 'bg-dark-950 border-dark-800'
            : 'bg-white border-gray-200',
        )}
      >
        {/* Drawer header */}
        <div className={cn(
          'flex items-center justify-between h-14 px-4 border-b',
          theme === 'dark' ? 'border-dark-800' : 'border-gray-200',
        )}>
          <span className={cn(
            'font-display font-semibold',
            theme === 'dark' ? 'text-white' : 'text-gray-900',
          )}>
            W2USDT <span className="text-uni-500">Bridge</span>
          </span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Cerrar menú"
            className={cn(
              'inline-flex items-center justify-center w-10 h-10 rounded-lg',
              theme === 'dark'
                ? 'text-dark-300 hover:text-white hover:bg-dark-800/50'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
            )}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Wallet section */}
        <div className={cn(
          'px-4 py-4 border-b',
          theme === 'dark' ? 'border-dark-800' : 'border-gray-200',
        )}>
          {isConnected ? (
            <div className="space-y-2">
              <div className={cn(
                'flex items-center gap-2 px-3 py-2.5 rounded-lg border',
                theme === 'dark'
                  ? 'bg-dark-800/80 border-dark-700 text-white'
                  : 'bg-gray-50 border-gray-200 text-gray-900',
              )}>
                <Wallet className="w-4 h-4 shrink-0" />
                <span className="font-mono text-sm truncate">{shortenAddress(address || '')}</span>
              </div>
              <button
                onClick={() => disconnect()}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-red-500/15 border border-red-500/20 text-red-500 hover:bg-red-500/25 transition-all text-sm font-medium"
              >
                <LogOut className="w-4 h-4" />
                {language === 'es' ? 'Desconectar' : 'Disconnect'}
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                open()
                setMobileMenuOpen(false)
              }}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-medium text-sm bg-gradient-to-r from-uni-600 to-uni-500 text-dark-950 shadow-glow-sm"
            >
              <Wallet className="w-4 h-4" />
              {t('connect')}
            </button>
          )}
        </div>

        {/* Nav links */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors',
                  isActive
                    ? 'bg-uni-500/15 text-uni-500'
                    : theme === 'dark'
                      ? 'text-dark-300 hover:text-white hover:bg-dark-800/50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
                )}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {item.label}
              </Link>
            )
          })}
          {showAdminButton && (
            <Link
              to="/admin/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg bg-yellow-500/15 text-yellow-400 hover:bg-yellow-500/25 transition-all text-base"
            >
              <Shield className="w-5 h-5 shrink-0" />
              {t('adminPanel')}
            </Link>
          )}
        </nav>

        {/* Preferences */}
        <div className={cn(
          'p-3 mt-2 border-t space-y-2',
          theme === 'dark' ? 'border-dark-800' : 'border-gray-200',
        )}>
          <button
            onClick={toggleTheme}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors',
              theme === 'dark'
                ? 'text-dark-300 hover:text-white hover:bg-dark-800/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
            )}
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            {theme === 'dark'
              ? language === 'es' ? 'Modo claro' : 'Light mode'
              : language === 'es' ? 'Modo oscuro' : 'Dark mode'}
          </button>

          <div className="flex gap-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code as 'es' | 'en')}
                className={cn(
                  'flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors',
                  language === lang.code
                    ? 'bg-uni-500/15 text-uni-500 ring-1 ring-uni-500/30'
                    : theme === 'dark'
                      ? 'text-dark-300 hover:bg-dark-800/50'
                      : 'text-gray-600 hover:bg-gray-100',
                )}
              >
                <span className="text-base">{lang.flag}</span>
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      </MobileDrawer>
    </div>
  )
}
